import React, { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// How many stories a week this learner gets. Everyone starts on one. A paid
// member can switch to two; a free account sees the option, locked, with a
// short note. Only learner accounts see it.
export default function StoriesPerWeekChoice() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user || user.role !== "student") return null;

  const current = user.stories_per_week || 1;
  const paid = !!user.membership_paid;

  const choose = async (n) => {
    if (n === current || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await client.put("/me/stories-per-week", { stories_per_week: n });
      setUser(data.user);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const option = (n, label, locked) => {
    const active = current === n;
    return (
      <button
        type="button"
        onClick={() => choose(n)}
        disabled={busy || locked}
        aria-pressed={active}
        data-testid={`stories-per-week-${n}`}
        className={`rounded-full px-4 py-2 text-sm font-bold min-h-[44px] transition ${
          active ? "bg-hv-navy text-white" : "bg-white text-hv-navy border-2 border-[#E5DFC8]"
        } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {label}
      </button>
    );
  };

  return (
    <section className="rounded-[2rem] bg-white shadow-sm p-5 sm:p-6" data-testid="stories-per-week">
      <div className="text-sm font-bold text-hv-navy">Stories each week</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {option(1, "1 story", false)}
        {option(2, "2 stories", !paid)}
      </div>
      <p className="mt-3 text-sm text-hv-slate" data-testid="stories-per-week-note">
        {!paid
          ? "Everyone starts with one story a week. The paid plan lets you add a second."
          : current === 2
            ? "Two stories a week: a second story unlocks each week, alongside the first."
            : "One story a week. Choose 2 stories any time to add a second."}
      </p>
      {err && <p className="mt-2 text-sm font-bold text-hv-coral" role="alert">{err}</p>}
    </section>
  );
}
